import { ModalType } from '~/stores/modal'
import { getParentShape, getShapeById } from '~/composables/shapes/utils'
import { GRID_MENU } from '~/composables/shapes/constants'
import { GRID_PATTERN_IDS, GRID_PATTERN_LIST, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import type { ShapeMetadata } from '~/types/grid.types'

type SelectionItem = {
  id: string
  metadata?: Partial<ShapeMetadata>
  attrs?: { metadata?: Partial<ShapeMetadata> }
}

const selectionMetadata = (item: SelectionItem) =>
  item.metadata || item.attrs?.metadata

const shapeFamilyKey = (item: SelectionItem) => {
  const metadata = selectionMetadata(item)
  return metadata?.shape_label?.trim() || metadata?.shape_id?.trim() || null
}

const pluralizeShapeLabel = (label: string) => {
  if (/[^aeiou]y$/i.test(label)) return `${label.slice(0, -1)}ies`
  if (/(s|x|z|ch|sh)$/i.test(label)) return `${label}es`
  return `${label}s`
}

export function getShapeSelectionScope(items: SelectionItem[]) {
  if (!items.length) return null
  const key = shapeFamilyKey(items[0])
  if (!key || items.some(item => shapeFamilyKey(item) !== key)) return null

  const metadata = selectionMetadata(items[0])
  const shape = metadata?.shape_id
    ? getParentShape(metadata.shape_id) || getShapeById(metadata.shape_id)
    : undefined
  const label = shape?.label || metadata?.shape_label || metadata?.shape_id
  return label ? { key, label: pluralizeShapeLabel(label) } : null
}

export function getShapeSelectionIds(
  shapeList: SelectionItem[],
  selectedItems: SelectionItem[],
) {
  const scope = getShapeSelectionScope(selectedItems)
  return (scope
    ? shapeList.filter(item => shapeFamilyKey(item) === scope.key)
    : shapeList
  ).map(item => item.id)
}

type ShapeInteractionHandlers = {
  handleColorClick: (color: StudioColor, event: Event) => void
  handleShapeClick: (shape: StudioShape) => void
  handleParentShapeClick: (shape: StudioShape) => void
  closeShapeGroup: () => void
}

type StudioColor = { label: string; admin?: boolean; active?: boolean; [key: string]: unknown }
type StudioShape = {
  id: string
  icon: string
  label: string
  admin?: boolean
  active?: boolean
  sectionTitle?: string
  sectionSubtitle?: string
  breakpoint?: boolean
  children?: StudioShape[]
  [key: string]: unknown
}
type StudioShapeGroup = { items: StudioShape[]; [key: string]: unknown }
type ShapeCatalogItem = { id: string; children?: ShapeCatalogItem[]; [key: string]: unknown }

type FlagResolver = (id: string, admin?: boolean, active?: boolean) => {
  admin: boolean
  active: boolean
}

type ModalStoreLike = {
  modals: Record<string, { visible: boolean }>
  getModalOptions: (type: ModalType) => unknown
  getModalCallback: (type: ModalType) => unknown
  toggleModal: (type: ModalType) => void
}
type PatternModalStoreLike = {
  modals: Record<string, { visible: boolean }>
  toggleModal: (type: ModalType, payload?: { options?: Record<string, unknown> }) => void
}

export function createPickerInteractions(interactions: ShapeInteractionHandlers, onShapeSelected: () => void = () => {}) {
  const releasePointerFocus = (event?: MouseEvent) => {
    if (!event?.detail) return
    ;(event.currentTarget as HTMLElement | null)?.blur?.()
  }

  return {
    selectColor: (color: StudioColor, event: Event) => interactions.handleColorClick(color, event),
    selectShape: (shape: StudioShape, event?: MouseEvent) => {
      if (shape.children?.length) interactions.handleParentShapeClick(shape)
      else {
        interactions.handleShapeClick(shape)
        interactions.closeShapeGroup()
      }
      onShapeSelected()
      releasePointerFocus(event)
    },
    releasePointerFocus,
  }
}

export function buildPatternShapeCatalog<T extends ShapeCatalogItem>(patternId: string, shapesByPattern: Record<string, T[]>) {
  const catalog: Record<string, T> = {}
  const add = (shape: T, overwrite: boolean) => {
    if (overwrite || !catalog[shape.id]) catalog[shape.id] = shape
    for (const child of shape.children || []) {
      if (overwrite || !catalog[child.id]) catalog[child.id] = child as T
    }
  }
  for (const shapes of Object.values(shapesByPattern)) {
    for (const shape of shapes) add(shape, false)
  }
  for (const shape of shapesByPattern[patternId] || []) add(shape, true)
  return catalog
}

export function filterStudioColors<T extends StudioColor>({
  colors,
  getColorFlags,
  isAdmin,
  patternAllowlist,
  hasVariation = () => true,
}: {
  colors: T[]
  getColorFlags: FlagResolver
  isAdmin: boolean
  patternAllowlist?: string[] | null
  hasVariation?: (label: string) => boolean
}) {
  return colors.filter((color) => {
    const flags = getColorFlags(color.label, color.admin, color.active)
    if (!flags.active || (flags.admin && !isAdmin)) return false
    if (patternAllowlist) return patternAllowlist.includes(color.label)
    return hasVariation(color.label)
  })
}

export function filterStudioShapeGroups({
  groups,
  getShapeFlags,
  isAdmin,
}: {
  groups: StudioShapeGroup[]
  getShapeFlags: FlagResolver
  isAdmin: boolean
}) {
  const isVisible = (shape: StudioShape) => {
    const flags = getShapeFlags(shape.id, shape.admin, shape.active)
    return flags.active && (!flags.admin || isAdmin)
  }

  return groups.flatMap((group) => {
    const items = group.items.flatMap((item) => {
      if (item.sectionTitle || item.sectionSubtitle || item.breakpoint) return [item]
      if (!isVisible(item)) return []
      if (!item.children) return [item]
      return [{ ...item, children: item.children.filter(isVisible) }]
    })
    const hasShape = items.some((item) => !item.sectionTitle && !item.sectionSubtitle && !item.breakpoint)
    return hasShape ? [{ ...group, items }] : []
  })
}

export function hasValidDesignName(name: string | null | undefined) {
  return Boolean(name?.trim())
}

export function createProjectNameGuard(initialName: string | null | undefined, showError: (message: string) => void) {
  let previousValidName = initialName || ''
  return {
    accept(nextName: string | null | undefined) {
      const next = nextName || ''
      if (next.toLowerCase().includes('custom')) {
        showError("Design name cannot contain the word 'Custom'")
        return previousValidName
      }
      previousValidName = next
      return next
    },
  }
}

/** @deprecated Grid options are now handled by the GridOptionsPopover inline in CanvasControls */
export function openGridOptions(modalStore: ModalStoreLike) {
  modalStore.toggleModal(ModalType.GridSize)
}

export function openGridPatternSettings(modalStore: PatternModalStoreLike) {
  if (modalStore.modals[ModalType.GridSize].visible) modalStore.toggleModal(ModalType.GridSize)
  modalStore.toggleModal(ModalType.New, {
    options: { activeContent: 'patternList', showGridWarning: true },
  })
}

export function applyGridPatternChange<Pattern>(
  gridStore: {
    gridWidth: number
    gridHeight: number
    $reset: () => void
    setGridSize: (width: number, height: number) => void
    setGridPattern: (pattern: Pattern) => void
  },
  pattern: Pattern,
  { preserveDimensions }: { preserveDimensions: boolean },
) {
  const dimensions = { width: gridStore.gridWidth, height: gridStore.gridHeight }
  gridStore.$reset()
  if (preserveDimensions) gridStore.setGridSize(dimensions.width, dimensions.height)
  gridStore.setGridPattern(pattern)
}

type GridSwitchContext = {
  shapeListLength: number
  onConfirm: () => void
  onWarn: () => void
}

export function buildGridSwitchContext({
  gridStore,
  onConfirm,
  onWarn,
}: {
  gridStore: { shapeList: unknown[] }
  onConfirm: () => void
  onWarn: () => void
}): GridSwitchContext {
  return {
    shapeListLength: gridStore.shapeList.length,
    onConfirm,
    onWarn,
  }
}

export function confirmSwitchTo3DGrid({
  gridStore,
  projectStore,
  router,
  deleteCookie,
}: {
  gridStore: {
    setGridPattern: (pattern: unknown) => void
    updateSelectedTool: (tool: string) => void
    setSelectedShape: (shape: unknown) => void
    setTempItemByCurrentSelection: () => void
  }
  projectStore: {
    $reset: () => void
    setGridRef: (ref: unknown) => void
    gridRef: unknown
  }
  router: { replace: (location: { query: Record<string, never> }) => void }
  deleteCookie: (name: string) => void
}) {
  const pattern = GRID_PATTERN_LIST.find(p => p.id === GRID_PATTERN_IDS.THREE_D)
  if (!pattern) return
  const defaultShape = GRID_MENU[pattern.id]?.default
  if (!defaultShape) return
  const gridRef = projectStore.gridRef
  applyGridPatternChange(gridStore, pattern, { preserveDimensions: true })
  projectStore.$reset()
  projectStore.setGridRef(gridRef)
  router.replace({ query: {} })
  deleteCookie('studio_design_id')
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  gridStore.setSelectedShape(defaultShape)
  gridStore.setTempItemByCurrentSelection()
}

export function shouldWarnBefore3DSwitch(gridStore: { shapeList: unknown[] }) {
  return gridStore.shapeList.length > 0
}

export async function completeNewStudioModal(modalStore: ModalStoreLike, type: ModalType) {
  const callback = modalStore.getModalCallback(type)
  if (typeof callback === 'function') await callback()
  if (modalStore.modals[type].visible) modalStore.toggleModal(type)
}

export async function continueWithoutSaving(modalStore: ModalStoreLike, type: ModalType) {
  const options = modalStore.getModalOptions(type) as { onContinueWithoutSaving?: unknown } | null
  const callback = options?.onContinueWithoutSaving
  if (typeof callback === 'function') await callback()
  if (modalStore.modals[type].visible) modalStore.toggleModal(type)
}

export async function completeNewStudioSave(
  modalStore: ModalStoreLike,
  loadingStore: { setLoading: (loading: boolean) => void },
  { keepLoading }: { keepLoading: boolean },
) {
  await completeNewStudioModal(modalStore, ModalType.SaveAs)
  if (!keepLoading) loadingStore.setLoading(false)
}

export async function runGalleryRequest(
  request: () => Promise<unknown>,
  error: { value: string },
  message: string,
) {
  error.value = ''
  try {
    await request()
    return true
  } catch {
    error.value = message
    return false
  }
}

export async function copyTextWithFallback(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  }
  catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    try {
      return document.execCommand('copy')
    }
    catch {
      return false
    }
    finally {
      textarea.remove()
    }
  }
}

type CanvasHostResizeAdapterOptions = {
  createObserver?: (callback: ResizeObserverCallback) => ResizeObserver
  requestFrame?: (callback: FrameRequestCallback) => number
  cancelFrame?: (handle: number) => void
  dispatchResize?: () => void
}

export function createCanvasHostResizeAdapter(
  host: Element,
  {
    createObserver = callback => new ResizeObserver(callback),
    requestFrame = callback => requestAnimationFrame(callback),
    cancelFrame = handle => cancelAnimationFrame(handle),
    dispatchResize = () => window.dispatchEvent(new Event('resize')),
  }: CanvasHostResizeAdapterOptions = {},
) {
  let frame: number | null = null
  const observer = createObserver(() => {
    if (frame !== null) return
    frame = requestFrame(() => {
      frame = null
      dispatchResize()
    })
  })

  observer.observe(host)

  return () => {
    observer.disconnect()
    if (frame !== null) {
      cancelFrame(frame)
      frame = null
    }
  }
}
